import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  MapPin, 
  Star, 
  Award, 
  Globe, 
  DollarSign,
  Filter,
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  Shield,
  FileText,
  Calendar
} from 'lucide-react';

const VerifierDirectory = () => {
  const [verifiers, setVerifiers] = useState([]);
  const [filteredVerifiers, setFilteredVerifiers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    standards: [],
    regions: [],
    projectTypes: [],
    ratingRange: '',
    experienceLevel: '',
    priceRange: '',
    sortBy: 'rating'
  });
  const [loading, setLoading] = useState(true);

  // Mock verifier data
  const mockVerifiers = [
    {
      id: "VER-001",
      companyName: "Carbon Integrity Ltd",
      profileImage: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=400&fit=crop",
      verified: true,
      rating: 4.9,
      reviewCount: 156,
      completedProjects: 220,
      yearsOfExperience: 15,
      responseTime: 12, // hours
      completionRate: 98,
      
      // Contact & Location
      primaryContact: "Dr. Priya Sharma",
      location: "Mumbai, India",
      geographicCoverage: ["Asia", "Africa", "Europe"],
      website: "https://carbonintegrity.com",
      email: "contact@carbonintegrity.com",
      
      // Capabilities
      supportedStandards: ["Gold Standard", "VCS", "Plan Vivo", "CDM"],
      projectTypes: [
        "Forest Conservation", "Renewable Energy", "Blue Carbon", 
        "Energy Efficiency", "Methane Capture"
      ],
      technicalCapabilities: [
        "Remote Sensing & GIS", "Field Surveys", "Laboratory Analysis",
        "Satellite Monitoring", "Environmental Modeling"
      ],
      
      // Credentials
      accreditations: [
        "ISO 14065:2020", "ANSI National Accreditation Board", 
        "International Association of Environmental Auditors"
      ],
      certifications: [
        "Certified Carbon Verifier", "Environmental Management Systems Lead Auditor"
      ],
      
      // Pricing & Commercial
      pricingModel: "Project-based",
      averagePrice: 0.42,
      currency: "USD",
      paymentTerms: "Net 30 days",
      
      // Performance Metrics
      onTimeDelivery: 96,
      clientRetention: 89,
      averageProjectValue: 15500,
      totalVerificationValue: 3410000,
      
      // Recent Reviews
      recentReviews: [
        {
          projectTitle: "Amazon Conservation Project",
          rating: 5,
          comment: "Exceptional attention to detail and professional service.",
          client: "EcoForest Initiative",
          date: "2024-01-15"
        },
        {
          projectTitle: "Solar Farm Verification",
          rating: 5,
          comment: "Thorough verification process with clear communication.",
          client: "GreenPower Solutions",
          date: "2024-01-10"
        }
      ],
      
      // Specializations
      specializations: ["REDD+ Projects", "Renewable Energy", "Community-based Conservation"],
      languages: ["English", "Hindi", "German"],
      
      // Availability
      availability: "Available",
      nextAvailableSlot: "2024-02-15",
      currentWorkload: 75 // percentage
    },
    {
      id: "VER-002",
      companyName: "EcoVerify International",
      profileImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop",
      verified: true,
      rating: 4.8,
      reviewCount: 203,
      completedProjects: 185,
      yearsOfExperience: 12,
      responseTime: 8,
      completionRate: 94,
      
      primaryContact: "Dr. Sarah Mitchell",
      location: "San Francisco, USA",
      geographicCoverage: ["North America", "Latin America", "Europe"],
      website: "https://ecoverify.com",
      email: "contact@ecoverify.com",
      
      supportedStandards: ["Gold Standard", "VCS", "ACR", "CAR"],
      projectTypes: [
        "Forest Conservation", "Renewable Energy", "Energy Efficiency",
        "Transportation", "Industrial Processes"
      ],
      technicalCapabilities: [
        "Drone Technology", "IoT Sensors", "Blockchain Verification",
        "AI/ML Analytics", "Field Surveys"
      ],
      
      accreditations: [
        "ISO 14065:2020", "ANSI National Accreditation Board"
      ],
      certifications: [
        "Certified Carbon Verifier", "GHG Verification Professional"
      ],
      
      pricingModel: "Milestone-based",
      averagePrice: 0.38,
      currency: "USD",
      paymentTerms: "50% upfront, 50% completion",
      
      onTimeDelivery: 92,
      clientRetention: 85,
      averageProjectValue: 12800,
      totalVerificationValue: 2368000,
      
      recentReviews: [
        {
          projectTitle: "Wind Energy Verification",
          rating: 5,
          comment: "Highly professional team with cutting-edge technology.",
          client: "WindPower Corp",
          date: "2024-01-18"
        }
      ],
      
      specializations: ["Technology Integration", "Large-scale Projects", "Methodology Development"],
      languages: ["English", "Spanish", "Portuguese"],
      
      availability: "Busy",
      nextAvailableSlot: "2024-03-01",
      currentWorkload: 95
    },
    {
      id: "VER-003",
      companyName: "GreenCert Solutions",
      profileImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      verified: true,
      rating: 4.6,
      reviewCount: 89,
      completedProjects: 127,
      yearsOfExperience: 8,
      responseTime: 18,
      completionRate: 91,
      
      primaryContact: "Michael Rodriguez",
      location: "Mexico City, Mexico",
      geographicCoverage: ["Latin America", "North America"],
      website: "https://greencert.org",
      email: "contact@greencert.org",
      
      supportedStandards: ["VCS", "ACR", "CAR", "Social Carbon"],
      projectTypes: [
        "Forest Conservation", "Blue Carbon", "Soil Carbon",
        "Waste Management", "Agriculture"
      ],
      technicalCapabilities: [
        "Social Impact Assessment", "Community Engagement",
        "Field Surveys", "Environmental Modeling"
      ],
      
      accreditations: ["ISO 14065:2020"],
      certifications: ["Certified Carbon Verifier"],
      
      pricingModel: "Project-based",
      averagePrice: 0.35,
      currency: "USD",
      paymentTerms: "Net 15 days",
      
      onTimeDelivery: 88,
      clientRetention: 82,
      averageProjectValue: 9200,
      totalVerificationValue: 1168400,
      
      recentReviews: [
        {
          projectTitle: "Mangrove Restoration",
          rating: 4,
          comment: "Good local expertise and community connections.",
          client: "Coastal Conservation",
          date: "2024-01-12"
        }
      ],
      
      specializations: ["Community Projects", "Blue Carbon", "Latin American Markets"],
      languages: ["Spanish", "English", "Portuguese"],
      
      availability: "Available",
      nextAvailableSlot: "2024-02-20",
      currentWorkload: 60
    }
  ];

  const allStandards = ["Gold Standard", "VCS", "CDM", "ACR", "CAR", "Plan Vivo", "Social Carbon"];
  const allRegions = ["North America", "Latin America", "Europe", "Asia", "Africa", "Oceania"];
  const allProjectTypes = [
    "Forest Conservation", "Renewable Energy", "Energy Efficiency",
    "Blue Carbon", "Methane Capture", "Transportation", "Agriculture",
    "Waste Management", "Industrial Processes"
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setVerifiers(mockVerifiers);
      setFilteredVerifiers(mockVerifiers);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    filterVerifiers();
  }, [searchQuery, filters, verifiers]);

  const filterVerifiers = () => {
    let filtered = [...verifiers];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(verifier =>
        verifier.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        verifier.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        verifier.specializations.some(spec => 
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Standards filter
    if (filters.standards.length > 0) {
      filtered = filtered.filter(verifier =>
        filters.standards.some(standard =>
          verifier.supportedStandards.includes(standard)
        )
      );
    }

    // Regions filter
    if (filters.regions.length > 0) {
      filtered = filtered.filter(verifier =>
        filters.regions.some(region =>
          verifier.geographicCoverage.includes(region)
        )
      );
    }

    // Project types filter
    if (filters.projectTypes.length > 0) {
      filtered = filtered.filter(verifier =>
        filters.projectTypes.some(type =>
          verifier.projectTypes.includes(type)
        )
      );
    }

    // Rating filter
    if (filters.ratingRange) {
      const minRating = parseFloat(filters.ratingRange);
      filtered = filtered.filter(verifier => verifier.rating >= minRating);
    }

    // Experience filter
    if (filters.experienceLevel) {
      const minYears = parseInt(filters.experienceLevel);
      filtered = filtered.filter(verifier => verifier.yearsOfExperience >= minYears);
    }

    // Price range filter
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(parseFloat);
      filtered = filtered.filter(verifier => 
        verifier.averagePrice >= min && verifier.averagePrice <= max
      );
    }

    // Sort
    switch (filters.sortBy) {
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'experience':
        filtered.sort((a, b) => b.yearsOfExperience - a.yearsOfExperience);
        break;
      case 'projects':
        filtered.sort((a, b) => b.completedProjects - a.completedProjects);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.averagePrice - b.averagePrice);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.averagePrice - a.averagePrice);
        break;
      default:
        break;
    }

    setFilteredVerifiers(filtered);
  };

  const handleFilterChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (Array.isArray(newFilters[filterType])) {
        if (checked) {
          newFilters[filterType] = [...newFilters[filterType], value];
        } else {
          newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
        }
      } else {
        newFilters[filterType] = value;
      }
      
      return newFilters;
    });
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'Available':
        return 'text-green-600 bg-green-100';
      case 'Busy':
        return 'text-yellow-600 bg-yellow-100';
      case 'Unavailable':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Verifier Directory</h1>
        <p className="text-gray-600">Find certified carbon credit verifiers for your projects</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Verifiers</p>
              <p className="text-2xl font-bold text-gray-900">{verifiers.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {(verifiers.reduce((sum, v) => sum + v.rating, 0) / verifiers.length).toFixed(1)}
              </p>
            </div>
            <Star className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Projects Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {verifiers.reduce((sum, v) => sum + v.completedProjects, 0).toLocaleString()}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Now</p>
              <p className="text-2xl font-bold text-gray-900">
                {verifiers.filter(v => v.availability === 'Available').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filters
            </h3>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Company, location, or specialty..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Standards */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Standards</label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {allStandards.map(standard => (
                  <label key={standard} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.standards.includes(standard)}
                      onChange={(e) => handleFilterChange('standards', standard, e.target.checked)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{standard}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Regions */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Regions</label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {allRegions.map(region => (
                  <label key={region} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.regions.includes(region)}
                      onChange={(e) => handleFilterChange('regions', region, e.target.checked)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{region}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Rating</label>
              <select
                value={filters.ratingRange}
                onChange={(e) => handleFilterChange('ratingRange', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Any Rating</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
                <option value="3.5">3.5+ Stars</option>
              </select>
            </div>

            {/* Experience */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
              <select
                value={filters.experienceLevel}
                onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Any Experience</option>
                <option value="10">10+ Years</option>
                <option value="5">5+ Years</option>
                <option value="2">2+ Years</option>
              </select>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Price Range (USD/credit)</label>
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Any Price</option>
                <option value="0-0.3">$0.00 - $0.30</option>
                <option value="0.3-0.5">$0.30 - $0.50</option>
                <option value="0.5-1.0">$0.50 - $1.00</option>
                <option value="1.0-999">$1.00+</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Sort and Results */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">
              Showing {filteredVerifiers.length} of {verifiers.length} verifiers
            </p>
            
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="rating">Highest Rated</option>
              <option value="experience">Most Experience</option>
              <option value="projects">Most Projects</option>
              <option value="price-low">Lowest Price</option>
              <option value="price-high">Highest Price</option>
            </select>
          </div>

          {/* Verifier Cards */}
          <div className="space-y-6">
            {filteredVerifiers.map((verifier) => (
              <div key={verifier.id} className="bg-white rounded-lg shadow-sm border hover:shadow-lg transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    {/* Profile Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={verifier.profileImage}
                        alt={verifier.companyName}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    </div>

                    {/* Main Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <h3 className="text-xl font-semibold text-gray-900">{verifier.companyName}</h3>
                          {verifier.verified && (
                            <CheckCircle className="h-5 w-5 text-green-600 ml-2" />
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(verifier.availability)}`}>
                          {verifier.availability}
                        </span>
                      </div>

                      <div className="flex items-center mb-3">
                        <div className="flex items-center mr-4">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="ml-1 text-sm font-medium">{verifier.rating}</span>
                          <span className="ml-1 text-sm text-gray-600">({verifier.reviewCount} reviews)</span>
                        </div>
                        <div className="flex items-center mr-4">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="ml-1 text-sm text-gray-600">{verifier.location}</span>
                        </div>
                        <div className="flex items-center">
                          <Award className="h-4 w-4 text-gray-400" />
                          <span className="ml-1 text-sm text-gray-600">{verifier.yearsOfExperience} years</span>
                        </div>
                      </div>

                      <p className="text-gray-700 mb-4">
                        Contact: {verifier.primaryContact} • {verifier.completedProjects} projects completed
                      </p>

                      {/* Specializations */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {verifier.specializations.slice(0, 3).map((spec, index) => (
                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {spec}
                            </span>
                          ))}
                          {verifier.specializations.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{verifier.specializations.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Supported Standards */}
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">Supported Standards:</p>
                        <div className="flex flex-wrap gap-1">
                          {verifier.supportedStandards.map((standard, index) => (
                            <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {standard}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Pricing & Stats */}
                    <div className="flex-shrink-0 text-right">
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-2xl font-bold text-gray-900">${verifier.averagePrice}</p>
                        <p className="text-sm text-gray-600">per credit</p>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Response:</span>
                          <span className="font-medium">{verifier.responseTime}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Completion:</span>
                          <span className="font-medium">{verifier.completionRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Workload:</span>
                          <span className="font-medium">{verifier.currentWorkload}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="font-semibold text-gray-900">{verifier.onTimeDelivery}%</p>
                      <p className="text-gray-600">On-time delivery</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="font-semibold text-gray-900">{verifier.clientRetention}%</p>
                      <p className="text-gray-600">Client retention</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="font-semibold text-gray-900">${verifier.averageProjectValue.toLocaleString()}</p>
                      <p className="text-gray-600">Avg. project value</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="font-semibold text-gray-900">{verifier.geographicCoverage.length}</p>
                      <p className="text-gray-600">Regions covered</p>
                    </div>
                  </div>

                  {/* Recent Review */}
                  {verifier.recentReviews && verifier.recentReviews.length > 0 && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < verifier.recentReviews[0].rating
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {verifier.recentReviews[0].client}
                        </span>
                        <span className="ml-2 text-sm text-gray-600">
                          • {new Date(verifier.recentReviews[0].date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 italic">
                        "{verifier.recentReviews[0].comment}"
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-6 flex space-x-3">
                    <Link
                      to={`/verifiers/${verifier.id}`}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-center py-2 px-4 rounded-lg font-medium transition duration-200"
                    >
                      View Profile
                    </Link>
                    <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition duration-200">
                      Request Quote
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-200">
                      <Star className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredVerifiers.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No verifiers found</h3>
              <p className="mt-1 text-gray-500">Try adjusting your filters to see more results.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifierDirectory; 